import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { GlassDock, GlassFilter } from './liquid-glass';
import { AddToDockDialog } from './AddToDockDialog';
import { WindowThumbnail } from './window-thumbnail';
import { usePreferences } from '@/contexts/PreferencesContext';
import { useWindowManager } from '@/contexts/WindowManager';
import { useAppsManager } from '@/contexts/AppsManager';
import { useToast } from '@/hooks/use-toast';
import { useChatUnreadCount } from '@/hooks/useChatUnreadCount';
import { useSoundstageUpcomingBadge } from '@/hooks/useSoundstageUpcomingBadge';
import { isAppStoreApp } from '@/lib/apps';
import NotesWindow from '@/components/windows/NotesWindow';
import AppStoreWindow from '@/components/windows/AppStoreWindow';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { DynamicIcon } from './dynamic-icon';
import { X } from 'lucide-react';
import Lottie from 'lottie-react';
import confettiAnimation from '@/assets/animations/confetti.json';
import tapBurstAnimation from '@/assets/animations/tap-burst.json';

interface OSDockProps {
  kpis?: any;
}

export function OSDock({ kpis }: OSDockProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const windowManager = useWindowManager();
  const { openWindow, isWindowOpen, restoreWindow, minimizeWindow, windows } = windowManager;
  const {
    apps,
    dockApps,
    runningApps,
    unifiedDock,
    reorderUnifiedDock,
    addToDock,
    removeFromDock,
    removeFromUnifiedDock,
  } = useAppsManager();
  const { dockCustomization, timeMode } = usePreferences();
  const { toast } = useToast();
  const { totalUnread: chatUnreadCount, displayCount: chatDisplayCount } = useChatUnreadCount();
  const { upcomingCount: soundstageUpcomingCount, displayCount: soundstageDisplayCount } = useSoundstageUpcomingBadge();

  // Auto-hide dock states
  const [isVisible, setIsVisible] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const [showAddToDockDialog, setShowAddToDockDialog] = useState(false);
  const [appToAdd, setAppToAdd] = useState<{ id: string; name: string } | null>(null);
  const hideTimer = useRef<NodeJS.Timeout | null>(null);
  const showTimer = useRef<NodeJS.Timeout | null>(null);

  // Dock edit mode states
  const [isEditMode, setIsEditMode] = useState(false);
  const dockContainerRef = useRef<HTMLDivElement>(null);
  const doubleClickTimerRef = useRef<NodeJS.Timeout | null>(null);
  const clickCountRef = useRef(0);

  // Check if any window is maximized
  const hasMaximizedWindow = windows.some((window) => window.isMaximized && !window.isMinimized);

  // Auto-hide logic - but NOT during edit mode
  useEffect(() => {
    // Clear existing timers
    if (hideTimer.current) clearTimeout(hideTimer.current);
    if (showTimer.current) clearTimeout(showTimer.current);

    // Never hide during edit mode
    if (isEditMode) {
      setIsVisible(true);
      return;
    }

    if (isHovered) {
      // Show dock immediately on hover
      setIsVisible(true);
    } else if (!hasMaximizedWindow) {
      // Auto-hide after 15 seconds of no hover (only if no maximized windows)
      hideTimer.current = setTimeout(() => {
        setIsVisible(false);
      }, 15000);
    }

    return () => {
      if (hideTimer.current) clearTimeout(hideTimer.current);
      if (showTimer.current) clearTimeout(showTimer.current);
    };
  }, [isHovered, hasMaximizedWindow, isEditMode]);

  // Show dock when mouse approaches bottom of screen
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (e.clientY > window.innerHeight - 100 && !isVisible && !hasMaximizedWindow) {
        setIsVisible(true);
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [isVisible, hasMaximizedWindow]);

  // Get minimized windows for display (limit to 5)
  const minimizedWindows = windows.filter((window) => window.isMinimized).slice(0, 5);
  const hasMinimizedWindows = minimizedWindows.length > 0;

  // Helper function to check if a window is minimized
  const isWindowMinimized = (windowId: string): boolean => {
    const window = windows.find((w) => w.id === windowId);
    return window ? window.isMinimized : false;
  };

  // Handle window click - restore if minimized, focus if open, or open if closed
  const handleWindowClick = (windowId: string, title: string, component: React.ReactNode) => {
    if (isWindowMinimized(windowId)) {
      // Window is minimized, restore it
      restoreWindow(windowId);
    } else if (isWindowOpen(windowId)) {
      // Window is open but not minimized, bring to focus (already handled by openWindow)
      openWindow(windowId, title, component);
    } else {
      openWindow(windowId, title, component);
    }
  };

  // Clean workbench function - minimize all open windows
  const handleCleanWorkbench = () => {
    windows.forEach((window) => {
      if (!window.isMinimized) {
        minimizeWindow(window.id);
      }
    });
  };

  // Animation state for removing apps
  const [removingAppId, setRemovingAppId] = useState<string | null>(null);
  const [confettiPosition, setConfettiPosition] = useState<{ x: number; y: number } | null>(null);
  const [hidingAppId, setHidingAppId] = useState<string | null>(null);

  // Animation state for adding apps
  const [justAddedIds, setJustAddedIds] = useState<Set<string>>(new Set());
  const prevDockIdsRef = useRef<string[]>([]);
  const dockItemRefs = useRef(new Map<string, HTMLDivElement | null>());
  const [tapBurst, setTapBurst] = useState<{ visible: boolean; x: number; y: number }>({
    visible: false,
    x: 0,
    y: 0,
  });

  // Dock edit mode handlers
  const enterEditMode = () => {
    setIsEditMode(true);
    setIsVisible(true); // Keep dock visible during edit
    toast({
      title: 'Customize Dock',
      description: 'Drag to rearrange. Click ✕ to remove apps.',
      duration: 3000,
    });
  };

  const exitEditMode = () => {
    setIsEditMode(false);
  };

  // Handle removing app from dock - handles both static items and database apps
  const handleRemoveFromDock = async (itemId: string, itemName: string, e: React.MouseEvent) => {
    e.stopPropagation();

    console.log('🗑️ Removing from dock:', itemId, itemName);

    // Get position for confetti animation BEFORE removal
    const button = e.currentTarget as HTMLElement;
    const target = button.parentElement; // This is the dock item div
    if (target) {
      const rect = target.getBoundingClientRect();
      setConfettiPosition({
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
      });
    }

    // Start hiding the icon immediately for seamless animation
    setHidingAppId(itemId);

    // Delay the actual removal to sync with animation peak (around 300ms)
    setTimeout(async () => {
      // Handle static items (notes) separately - use removeFromUnifiedDock
      if (itemId === 'notes') {
        console.log('📍 Removing static item (notes) from unified dock order...');
        removeFromUnifiedDock(itemId);
        toast({
          title: 'Removed',
          description: `${itemName} removed from dock`,
          duration: 2000,
        });
      } else {
        // Database apps - use removeFromDock
        console.log('📍 Calling removeFromDock for database app...');
        await removeFromDock(itemId);
        console.log('✅ removeFromDock called');
      }

      // Clear hiding state after removal completes
      setHidingAppId(null);
    }, 300); // Sync with animation burst moment

    // Clear confetti animation after it completes
    setTimeout(() => {
      setConfettiPosition(null);
    }, 2000); // Extended for better visual
  };

  // Check if an item is removable
  const isRemovable = (itemId: string) => {
    // Only home (Clean Workbench) is not removable from static items
    if (itemId === 'home') {
      return false;
    }

    // Check if it's the App Store - also not removable
    const app = apps.find((a) => a.id === itemId);
    if (app && isAppStoreApp(app)) {
      return false;
    }

    // Everything else (including notes, system apps, etc.) can be removed from dock
    return true;
  };

  // Escape key to exit edit mode
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isEditMode) {
        exitEditMode();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isEditMode]);

  // Click outside to exit edit mode
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        isEditMode &&
        dockContainerRef.current &&
        !dockContainerRef.current.contains(e.target as Node)
      ) {
        exitEditMode();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isEditMode]);

  // Convert unified dock to dock icons with proper handlers
  const dockIcons = unifiedDock.map((item, index) => {
    if (item.id === 'home') {
      return {
        id: 'home',
        icon: <DynamicIcon name="home" className="w-6 h-6 block" />,
        alt: 'Dashboard',
        label: 'Clean Workbench',
        active: false, // Never active - it's always the background
        onClick: handleCleanWorkbench, // Minimize all windows
        isRunning: false,
        isPermanent: true,
        minimized: false,
        removable: false,
      };
    }
    if (item.id === 'notes') {
      return {
        id: 'notes',
        icon: <DynamicIcon name="notes" className="w-6 h-6 block" />,
        alt: 'Notes',
        label: 'Notes & Documents',
        active: isWindowOpen('notes') && !isWindowMinimized('notes'),
        minimized: isWindowMinimized('notes'),
        onClick: () => {
          handleWindowClick('notes', 'Notes & Documents', <NotesWindow />);
        },
        isRunning: false,
        isPermanent: true,
        removable: true, // Notes CAN be removed from dock
      };
    }
    // App items - use icon and component from unified dock (already prepared by AppsManager)
    const getIconName = (appId: string) => {
      // For static apps, use hardcoded mapping
      if (appId === 'inbox') return 'mail';

      // For dynamic apps from database, use icon_name field directly
      const app = apps.find((app) => app.id === appId);
      if (app?.icon_name) {
        // Convert PascalCase to kebab-case for Lucide icons
        const iconName = app.icon_name.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
        return iconName;
      }

      // Legacy fallback
      return 'store';
    };

    // Check if this is the Team Chat or SoundStage app for badge
    const appDef = apps.find((app) => app.id === item.id);
    const isTeamChat = appDef?.component_type === 'TeamChatWindow';
    const isSoundStage = appDef?.component_type === 'SoundStageWindow';

    return {
      id: item.id,
      icon: <DynamicIcon name={getIconName(item.id)} className="w-6 h-6 block" />,
      alt: item.name,
      label: item.name,
      active: isWindowOpen(item.id) && !isWindowMinimized(item.id),
      minimized: isWindowMinimized(item.id),
      isRunning: false,
      isPermanent: true,
      removable: isRemovable(item.id),
      badge: isTeamChat && chatUnreadCount > 0
        ? chatDisplayCount
        : isSoundStage && soundstageUpcomingCount > 0
        ? soundstageDisplayCount
        : undefined,
      onClick: () => {
        // In edit mode, clicking does nothing
        if (isEditMode) return;

        // Use component from unified dock (prepared by AppsManager)
        if (item.component) {
          console.log('🚀 Opening app from dock:', item.name, item.id);
          handleWindowClick(item.id, item.name, item.component);
        } else {
          console.warn('⚠️ No component found for app:', item.name, item.id);
        }
      },
    };
  });

  // Helper function for temporary running apps only (not in unified dock yet)
  const getAppComponentForTemp = (appId: string) => {
    // Find the app definition from AppsManager
    const appDef = apps.find((app) => app.id === appId);
    if (appDef) {
      // Use component from app definition if available
      const appFromUnified = unifiedDock.find((item) => item.id === appId);
      if (appFromUnified?.component) {
        return appFromUnified.component;
      }
      // Fallback: manually load based on component_type
      switch (appDef.component_type) {
        case 'AppStoreWindow':
          return <AppStoreWindow />;
        default:
          console.warn(
            '⚠️ Unknown component type for temp app:',
            appDef.name,
            appDef.component_type,
          );
          return null;
      }
    }
    return null;
  };

  // Get running apps that are not in unified dock
  const temporaryRunningApps = apps.filter(
    (app) =>
      runningApps.includes(app.id) && // Is running
      !unifiedDock.some((dockItem) => dockItem.id === app.id), // Not already in unified dock
  );

  // Create dock icons for temporary running apps
  const temporaryAppIcons = temporaryRunningApps.map((app) => {
    const getIconNameForTemp = (appData: typeof app) => {
      // Use icon_name from app data if available
      if (appData.icon_name) {
        // Convert PascalCase to kebab-case for Lucide icons
        const iconName = appData.icon_name.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
        return iconName;
      }

      // Legacy fallback for old hardcoded IDs
      switch (appData.id) {
        case 'calendar':
          return 'calendar';
        case 'settings':
          return 'settings';
        case 'camera':
          return 'camera';
        case 'music':
          return 'music';
        case 'video':
          return 'video';
        case 'calculator':
          return 'calculator';
        case 'clock':
          return 'clock';
        case 'maps':
          return 'map-pin';
        case 'phone':
          return 'phone';
        case 'messages':
          return 'message-square';
        case 'contacts':
          return 'user';
        case 'files':
          return 'file-text';
        case 'photos':
          return 'image';
        default:
          return 'store'; // fallback
      }
    };

    return {
      icon: <DynamicIcon name={getIconNameForTemp(app)} className="w-6 h-6 block" />,
      alt: app.name,
      label: app.name,
      active: isWindowOpen(app.id) && !isWindowMinimized(app.id),
      minimized: isWindowMinimized(app.id),
      isRunning: true, // Always show running indicator for temporary apps
      isPermanent: false,
      onClick: () => {
        const component = getAppComponentForTemp(app.id);
        if (component) {
          handleWindowClick(app.id, app.name, component);
        }
      },
    };
  });

  // Check if we have temporary apps to show separator
  const hasTemporaryApps = temporaryAppIcons.length > 0;

  // Get dock styling based on theme
  const getDockStyle = () => {
    const { theme, customColor } = dockCustomization;

    switch (theme) {
      case 'ocean-blue':
        return {
          background: 'rgba(30, 144, 255, 0.15)',
          border: '1px solid rgba(30, 144, 255, 0.4)',
          activeColor: '#1E90FF',
          runningColor: '#87CEEB',
          glow: '0 0 20px rgba(30, 144, 255, 0.3)',
        };
      case 'midnight':
        return {
          background: 'rgba(15, 15, 23, 0.3)',
          border: '1px solid rgba(75, 85, 99, 0.5)',
          activeColor: '#C0C0C0',
          runningColor: '#A0A0A0',
          glow: '0 0 20px rgba(192, 192, 192, 0.2)',
        };
      case 'custom':
        const color = customColor || '#f97316';
        // Parse hex color and create variations
        const r = parseInt(color.slice(1, 3), 16);
        const g = parseInt(color.slice(3, 5), 16);
        const b = parseInt(color.slice(5, 7), 16);
        return {
          background: `rgba(${r}, ${g}, ${b}, 0.15)`,
          border: `1px solid rgba(${r}, ${g}, ${b}, 0.4)`,
          activeColor: color,
          runningColor: `rgba(${r}, ${g}, ${b}, 0.8)`,
          glow: `0 0 20px rgba(${r}, ${g}, ${b}, 0.3)`,
        };
      default: // 'default' theme - Orange
        return {
          background: 'rgba(249, 115, 22, 0.15)',
          border: '1px solid rgba(249, 115, 22, 0.4)',
          activeColor: '#f97316',
          runningColor: '#fb923c',
          glow: '0 0 20px rgba(249, 115, 22, 0.3)',
        };
    }
  };

  const getIconClasses = (isActive: boolean, isRunning: boolean) => {
    const { iconStyle } = dockCustomization;

    let baseClasses = 'transition-all duration-300';

    // Apply icon style variations with more dramatic differences
    if (iconStyle === 'outlined') {
      baseClasses += ' stroke-[1]';
    } else if (iconStyle === 'minimal') {
      baseClasses += ' stroke-[0.5]';
    } else {
      baseClasses += ' stroke-[2] fill-current'; // filled
    }

    // Apply state-based styling with theme colors
    if (isActive) {
      baseClasses += ` scale-110`;
    } else if (isRunning) {
      baseClasses += ' scale-105';
    } else {
      baseClasses += ' scale-100 hover:scale-105';
    }

    return timeMode === 'rainbow' ? baseClasses + ' dock-rainbow-icon' : baseClasses;
  };

  const getIconColor = (isActive: boolean, isRunning: boolean) => {
    const style = getDockStyle();

    if (isActive) {
      return { color: style.activeColor };
    } else if (isRunning) {
      return { color: style.runningColor };
    } else {
      return { color: '#9CA3AF' }; // Gray for inactive
    }
  };

  const dockStyle = getDockStyle();

  // Handle drag end for dock reordering and moving temporary apps to permanent dock
  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    const sourceDroppableId = result.source.droppableId;
    const destinationDroppableId = result.destination.droppableId;
    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    // If dragging from temporary to permanent dock
    if (sourceDroppableId === 'temporary-dock' && destinationDroppableId === 'permanent-dock') {
      const tempApp = temporaryRunningApps[sourceIndex];
      if (tempApp) {
        setAppToAdd({ id: tempApp.id, name: tempApp.name });
        setShowAddToDockDialog(true);
      }
      return;
    }

    // If reordering within permanent dock
    if (sourceDroppableId === 'permanent-dock' && destinationDroppableId === 'permanent-dock') {
      reorderUnifiedDock(sourceIndex, destinationIndex);
      toast({
        title: 'Change accepted',
        description: 'Dock order updated successfully',
      });
    }
  };

  // Handle adding app to dock permanently
  const handleAddToDock = () => {
    if (appToAdd) {
      addToDock(appToAdd.id);
      toast({
        title: 'Added to Dock',
        description: `${appToAdd.name} has been added to your dock permanently.`,
      });
    }
    setShowAddToDockDialog(false);
    setAppToAdd(null);
  };

  // Handle canceling add to dock
  const handleCancelAddToDock = () => {
    setShowAddToDockDialog(false);
    setAppToAdd(null);
  };

  // Detect newly added apps to the dock for animation
  useEffect(() => {
    // Only consider actual apps (exclude static items like 'home' and 'notes')
    const currentAppIds = unifiedDock
      .filter((item) => (item as any).type === 'app')
      .map((item) => item.id);

    const prevAppIds = prevDockIdsRef.current;
    const added = currentAppIds.filter((id) => !prevAppIds.includes(id));

    if (added.length > 0) {
      added.forEach((id) => {
        // Mark as just added for scale-in animation
        setJustAddedIds((prev) => new Set(prev).add(id));

        // Position the Tap Burst over the icon center after layout
        requestAnimationFrame(() => {
          const el = dockItemRefs.current.get(id);
          if (el) {
            const rect = el.getBoundingClientRect();
            const cx = rect.left + rect.width / 2;
            const cy = rect.top + rect.height / 2;
            setTapBurst({ visible: true, x: cx, y: cy });
          }
        });

        // Clear the just-added flag after the animation plays
        setTimeout(() => {
          setJustAddedIds((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
        }, 500);
      });
    }

    // Update previous ids
    prevDockIdsRef.current = currentAppIds;
  }, [unifiedDock]);

  return (
    <>
      <GlassFilter />

      {/* Hover trigger area at bottom of screen - only show if no maximized windows */}
      {!hasMaximizedWindow && !isVisible && (
        <div
          className="fixed bottom-0 left-0 right-0 h-4 z-[9998]"
          onMouseEnter={() => setIsVisible(true)}
        />
      )}

      {/* Subtle dock indicator when hidden */}
      {!hasMaximizedWindow && !isVisible && (
        <div
          className="fixed bottom-2 left-1/2 transform -translate-x-1/2 z-[9998] transition-all duration-500 ease-out"
          onMouseEnter={() => setIsVisible(true)}
        >
          <div className="w-8 h-1 bg-white/20 rounded-full backdrop-blur-sm border border-white/10 shadow-sm hover:bg-white/30 hover:h-1.5 transition-all duration-200">
            <div className="w-full h-full bg-gradient-to-r from-white/0 via-white/30 to-white/0 rounded-full animate-gentle-glow" />
          </div>
        </div>
      )}

      {/* Apple-inspired Floating Dock with Dynamic Sizing */}
      <div
        className={`fixed bottom-6 left-1/2 transform -translate-x-1/2 z-[9999] transition-all duration-300 ease-out ${
          !isVisible || hasMaximizedWindow
            ? 'translate-y-20 opacity-0 pointer-events-none'
            : 'translate-y-0 opacity-100 pointer-events-auto'
        }`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="relative group" ref={dockContainerRef}>
          {/* Subtle edit button - top right, appears only when needed */}
          {!isEditMode && (
            <button
              onClick={enterEditMode}
              className="absolute -top-2 -right-2 z-10 w-5 h-5 rounded-full bg-background/40 backdrop-blur-xl border border-border/20 shadow-md hover:shadow-lg hover:bg-background/60 transition-all duration-200 hover:scale-110 active:scale-95 flex items-center justify-center opacity-0 group-hover:opacity-100"
              aria-label="Edit Dock"
            >
              <svg
                className="w-3 h-3 text-muted-foreground/60 hover:text-foreground transition-colors"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                />
              </svg>
            </button>
          )}

          {/* Done button for edit mode - simple and clean */}
          {isEditMode && (
            <button
              onClick={exitEditMode}
              className="absolute -top-10 left-1/2 -translate-x-1/2 z-10 px-5 py-1.5 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full shadow-lg transition-all duration-200 hover:scale-105 active:scale-95 font-medium text-sm"
            >
              Done
            </button>
          )}

          <DragDropContext onDragEnd={handleDragEnd}>
            <div
              className={`${timeMode === 'rainbow' ? 'dock-rainbow-mode ' : ''}
                relative flex items-center gap-1 p-3 rounded-2xl backdrop-blur-2xl shadow-xl 
                transition-all duration-500 ease-out hover:shadow-[0_8px_32px_rgba(0,0,0,0.6)]
                ${hasMinimizedWindows ? 'pr-2' : ''}
                ${isEditMode ? 'ring-2 ring-primary/30 scale-[1.02]' : ''}
              `}
              style={{
                background: getDockStyle().background,
                border: getDockStyle().border,
                boxShadow: `
                  0 4px 24px rgba(0, 0, 0, 0.4),
                  inset 0 1px 0 rgba(255, 255, 255, 0.08),
                  ${getDockStyle().glow}
                `,
              }}
            >
              {/* Permanent Dock Section */}
              <Droppable
                droppableId="permanent-dock"
                direction="horizontal"
                renderClone={(provided, snapshot, rubric) => {
                  const item = dockIcons[rubric.source.index];
                  return createPortal(
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      className="relative group/item pointer-events-none z-[100000]"
                      style={{ ...provided.draggableProps.style, zIndex: 100000 }}
                    >
                      <div className="translate-x-2 translate-y-2">
                        <button
                          className={`relative w-11 h-11 rounded-xl flex items-center justify-center leading-none transition-all duration-300 ease-out scale-110 rotate-2 ring-2 ring-primary/30 bg-glass/80 border border-border shadow-lg backdrop-blur-md`}
                        >
                          <div
                            className={getIconClasses(item.active, item.isRunning)}
                            style={getIconColor(item.active, item.isRunning)}
                          >
                            {item.icon}
                          </div>
                          {item.active && (
                            <div
                              className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-1 rounded-full"
                              style={{
                                backgroundColor: getDockStyle().activeColor,
                              }}
                            />
                          )}
                          {item.isRunning && !item.active && (
                            <div
                              className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full"
                              style={{
                                backgroundColor: getDockStyle().runningColor,
                              }}
                            />
                          )}
                          {item.minimized && (
                            <div className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-1.5 h-0.5 bg-warning/80 rounded-full" />
                          )}
                        </button>
                      </div>
                    </div>,
                    document.body,
                  );
                }}
              >
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className="flex items-center gap-1"
                  >
                    {/* Main Dock Items */}
                    {dockIcons.map((item, index) => (
                      <Draggable
                        key={`dock-${index}`}
                        draggableId={`dock-${index}`}
                        index={index}
                        isDragDisabled={index === 0} // Only disable drag for Home icon
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={(el) => {
                              // Preserve DnD ref
                              // @ts-ignore - provided.innerRef accepts HTMLElement | null
                              provided.innerRef(el);
                              // Track element per item id for animation positioning
                              if (el) {
                                dockItemRefs.current.set(item.id, el);
                              } else {
                                dockItemRefs.current.delete(item.id);
                              }
                            }}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            className={`relative group/item transition-all ease-out gpu-accelerate ${
                              hidingAppId === item.id
                                ? 'duration-300 scale-0 opacity-0'
                                : 'duration-300'
                            } ${snapshot.isDragging ? 'z-[10000] opacity-100' : ''} ${
                              isEditMode && item.removable && hidingAppId !== item.id
                                ? 'animate-dock-breathe motion-reduce:animate-none'
                                : ''
                            } ${removingAppId === item.id ? 'animate-trash-fly' : ''} ${
                              justAddedIds.has(item.id)
                                ? 'opacity-0 scale-90 animate-in fade-in-50 zoom-in-50'
                                : ''
                            }`}
                            style={{
                              ...provided.draggableProps.style,
                              ...(snapshot.isDragging ? { visibility: 'hidden' } : {}),
                              ...(hidingAppId === item.id
                                ? { transformOrigin: 'center bottom' }
                                : {}),
                            }}
                          >
                            {/* Minus button in edit mode - elegant and subtle */}
                            {isEditMode && item.removable && removingAppId !== item.id && (
                              <button
                                onClick={(e) => handleRemoveFromDock(item.id, item.label, e)}
                                className="absolute -top-2 -right-2 z-20 w-6 h-6 rounded-full bg-red-500/90 hover:bg-red-500 flex items-center justify-center shadow-xl backdrop-blur-md border border-white/30 transition-all duration-200 hover:scale-125 active:scale-95 opacity-0 group-hover/item:opacity-100"
                                aria-label={`Remove ${item.label} from dock`}
                              >
                                <svg
                                  className="w-3 h-3 text-white"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={3}
                                    d="M6 18L18 6M6 6l12 12"
                                  />
                                </svg>
                              </button>
                            )}

                            <button
                              onClick={item.onClick}
                              disabled={isEditMode}
                              className={`
                              relative w-11 h-11 rounded-xl
                              flex items-center justify-center leading-none
                              transition-all duration-300 ease-out
                              ${!isEditMode ? 'hover:scale-110' : ''}
                              active:scale-95 active:duration-75
                              ${
                                item.active
                                  ? 'bg-white/8 border border-white/15'
                                  : item.isRunning
                                    ? 'bg-white/4 border border-white/8 border-dashed'
                                    : ''
                              }
                              ${
                                snapshot.isDragging
                                  ? 'bg-white/30 border-2 border-white/60 shadow-[0_12px_40px_rgba(255,255,255,0.4)] ring-4 ring-white/20'
                                  : ''
                              }
                              ${isEditMode ? 'cursor-default' : 'cursor-pointer'}
                            `}
                              style={{
                                transformOrigin: 'bottom center',
                                ...(snapshot.isDragging && {
                                  boxShadow:
                                    '0 12px 40px rgba(255, 255, 255, 0.4), 0 0 30px rgba(255, 255, 255, 0.5), inset 0 2px 0 rgba(255, 255, 255, 0.2)',
                                  background:
                                    'linear-gradient(135deg, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0.1))',
                                  backdropFilter: 'blur(10px)',
                                }),
                              }}
                            >
                              {/* Icon - Clean and Minimal */}
                              <div
                                className={getIconClasses(item.active, item.isRunning)}
                                style={getIconColor(item.active, item.isRunning)}
                              >
                                {item.icon}
                              </div>

                              {/* Badge for unread count */}
                              {item.badge && (
                                <div className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full shadow-lg border border-white/20">
                                  {item.badge}
                                </div>
                              )}

                              {/* Theme-based active indicator - only for active windows */}
                              {item.active && (
                                <div className="absolute -bottom-1.5 left-1/2 transform -translate-x-1/2">
                                  <div
                                    className="w-1 h-1 rounded-full"
                                    style={{
                                      backgroundColor: getDockStyle().activeColor,
                                      boxShadow: `
                                         0 0 3px ${getDockStyle().activeColor},
                                         0 0 6px ${getDockStyle().activeColor}40
                                       `,
                                    }}
                                  />
                                </div>
                              )}

                              {/* Running indicator dot for temporary apps */}
                              {item.isRunning && !item.active && (
                                <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-white/40 rounded-full" />
                              )}

                              {/* Minimized indicator */}
                              {item.minimized && (
                                <div className="absolute -bottom-0.5 left-1/2 transform -translate-x-1/2 w-1.5 h-0.5 bg-amber-400/80 rounded-full" />
                              )}
                            </button>

                            {/* Tooltip */}
                            <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 px-2 py-1 bg-black/80 text-white text-xs rounded-md opacity-0 group-hover/item:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap">
                              {item.label}
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}

                    {provided.placeholder}
                  </div>
                )}
              </Droppable>

              {/* Separator between permanent dock apps and temporary running apps */}
              {hasTemporaryApps && <div className="w-px h-8 bg-white/20 mx-2" />}

              {/* Temporary Running Apps Section */}
              {hasTemporaryApps && (
                <Droppable
                  droppableId="temporary-dock"
                  direction="horizontal"
                  renderClone={(provided, snapshot, rubric) => {
                    const item = temporaryAppIcons[rubric.source.index];
                    return createPortal(
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className="relative group/item pointer-events-none z-[100000]"
                        style={{ ...provided.draggableProps.style, zIndex: 100000 }}
                      >
                        <div className="translate-x-2 translate-y-2">
                          <button
                            className={`relative w-11 h-11 rounded-xl flex items-center justify-center leading-none transition-all duration-300 ease-out scale-110 -rotate-2 ring-2 ring-primary/25 bg-glass/80 border border-border shadow-lg backdrop-blur-md`}
                          >
                            <div
                              className={getIconClasses(item.active, item.isRunning)}
                              style={getIconColor(item.active, item.isRunning)}
                            >
                              {item.icon}
                            </div>
                            {item.active && (
                              <div
                                className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full"
                                style={{
                                  backgroundColor: getDockStyle().activeColor,
                                }}
                              />
                            )}
                            {!item.active && (
                              <div
                                className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full"
                                style={{
                                  backgroundColor: getDockStyle().runningColor,
                                }}
                              />
                            )}
                            {item.minimized && (
                              <div className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-1.5 h-0.5 bg-warning/80 rounded-full" />
                            )}
                          </button>
                        </div>
                      </div>,
                      document.body,
                    );
                  }}
                >
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className="flex items-center gap-1"
                    >
                      {temporaryAppIcons.map((item, index) => (
                        <Draggable
                          key={`temp-${index}`}
                          draggableId={`temp-${index}`}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className={`relative group/item transition-all duration-200 ease-out ${
                                snapshot.isDragging ? 'z-[10000] opacity-100' : ''
                              }`}
                              style={{
                                ...provided.draggableProps.style,
                                ...(snapshot.isDragging ? { visibility: 'hidden' } : {}),
                              }}
                            >
                              <button
                                onClick={item.onClick}
                                className={`
                                  relative w-11 h-11 rounded-xl
                                  flex items-center justify-center leading-none
                                  transition-all duration-300 ease-out
                                  hover:scale-110
                                  active:scale-95 active:duration-75
                                  ${
                                    item.active
                                      ? 'bg-white/8 border border-white/20'
                                      : 'bg-white/3 border border-white/10 border-dotted'
                                  }
                                  ${
                                    snapshot.isDragging
                                      ? 'bg-white/25 border-2 border-white/50 shadow-[0_10px_32px_rgba(255,255,255,0.3)] ring-2 ring-white/15'
                                      : ''
                                  }
                                `}
                                style={{
                                  transformOrigin: 'bottom center',
                                  ...(snapshot.isDragging && {
                                    boxShadow:
                                      '0 10px 32px rgba(255, 255, 255, 0.3), 0 0 24px rgba(255, 255, 255, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.15)',
                                    background:
                                      'linear-gradient(135deg, rgba(255, 255, 255, 0.25), rgba(255, 255, 255, 0.08))',
                                    backdropFilter: 'blur(8px)',
                                  }),
                                }}
                              >
                                {/* Icon - Clean and Minimal with theming */}
                                <div
                                  className={getIconClasses(item.active, item.isRunning)}
                                  style={getIconColor(item.active, item.isRunning)}
                                >
                                  {item.icon}
                                </div>

                                {/* Theme-based indicator dot - only for active windows */}
                                {item.active && (
                                  <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 transition-all duration-300 ease-out">
                                    <div
                                      className="w-1 h-1 rounded-full"
                                      style={{
                                        backgroundColor: getDockStyle().activeColor,
                                      }}
                                    />
                                  </div>
                                )}

                                {/* Running indicator dot for temporary apps */}
                                {!item.active && (
                                  <div
                                    className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-1 h-1 rounded-full"
                                    style={{
                                      backgroundColor: getDockStyle().runningColor,
                                    }}
                                  />
                                )}

                                {/* Minimized indicator */}
                                {item.minimized && (
                                  <div className="absolute -bottom-0.5 left-1/2 transform -translate-x-1/2 w-1.5 h-0.5 bg-amber-400/80 rounded-full" />
                                )}
                              </button>

                              {/* Subtle tooltip for temporary apps */}
                              <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 px-2 py-1 bg-black/80 text-white/90 text-xs rounded-md opacity-0 group-hover/item:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap">
                                {item.label}
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              )}

              {/* Separator before minimized windows */}
              {hasMinimizedWindows && <div className="w-px h-8 bg-white/20 mx-2" />}

              {/* Minimized Windows Section */}
              {minimizedWindows.map((window, index) => (
                <div key={`minimized-${window.id}`} className="relative group/minimized">
                  <button
                    onClick={() => restoreWindow(window.id)}
                    className="relative w-11 h-8 rounded-lg overflow-hidden
                             border border-white/20 hover:border-primary/40 
                             transition-all duration-200 hover:scale-105
                             bg-muted/10 hover:bg-muted/20"
                    title={`Restore ${window.title}`}
                  >
                    <WindowThumbnail window={window} className="w-full h-full rounded-md" />
                  </button>

                  {/* Tooltip for minimized window */}
                  <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 px-2 py-1 bg-black/80 text-white text-xs rounded-md opacity-0 group-hover/minimized:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap">
                    {window.title}
                  </div>
                </div>
              ))}
            </div>
          </DragDropContext>

          {/* Subtle reflection effect */}
          <div
            className="absolute inset-x-0 -bottom-3 h-6 opacity-20 rounded-2xl blur-md"
            style={{
              background: `
                linear-gradient(135deg, 
                  rgba(255, 255, 255, 0.05) 0%,
                  rgba(255, 255, 255, 0.02) 50%,
                  rgba(0, 0, 0, 0.1) 100%
                )
              `,
            }}
          />
        </div>

        {/* Add to Dock Dialog */}
        <AddToDockDialog
          open={showAddToDockDialog}
          onOpenChange={setShowAddToDockDialog}
          appName={appToAdd?.name || ''}
          onConfirm={handleAddToDock}
          onCancel={handleCancelAddToDock}
        />
      </div>

      {/* Tap Burst animation for adding to dock */}
      {tapBurst.visible &&
        createPortal(
          <div
            className="fixed z-[100000] pointer-events-none"
            style={{
              left: tapBurst.x - 40,
              top: tapBurst.y - 40,
              width: 80,
              height: 80,
            }}
          >
            <Lottie
              animationData={tapBurstAnimation}
              loop={false}
              autoplay={true}
              style={{ width: '100%', height: '100%' }}
              rendererSettings={{ preserveAspectRatio: 'xMidYMid slice' }}
              onComplete={() => setTapBurst((prev) => ({ ...prev, visible: false }))}
            />
          </div>,
          document.body,
        )}

      {/* Elegant Lottie confetti animation */}
      {confettiPosition &&
        createPortal(
          <div
            className="fixed z-[100000] pointer-events-none"
            style={{
              left: confettiPosition.x,
              top: confettiPosition.y,
              transform: 'translate(-50%, -50%)',
            }}
          >
            <Lottie
              animationData={confettiAnimation}
              loop={false}
              autoplay={true}
              style={{
                width: 300, // Bigger animation
                height: 300,
              }}
              rendererSettings={{
                preserveAspectRatio: 'xMidYMid slice',
              }}
              onComplete={() => {
                // Clean up after animation completes
                setTimeout(() => setConfettiPosition(null), 100);
              }}
            />
          </div>,
          document.body,
        )}
    </>
  );
}
